import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { OrderDetail, OrderStatus } from '../types';

interface HeaderOrderDetailProps {
  order: OrderDetail;
  countdown: string;
  actionLoading: boolean;
  onApprove: () => void;
  onReject: () => void;
}

export const HeaderOrderDetail: React.FC<HeaderOrderDetailProps> = ({
  order,
  countdown,
  actionLoading,
  onApprove,
  onReject,
}) => {
  const translations = useSelector(selectTranslations);
  const getPassengerCount = (): string => {
    // Tính từ passengers array nếu có
    if (order.passengers && order.passengers.length > 0) {
      const adults = order.passengers.filter(
        p => p.passengerType === 'ADT',
      ).length;
      const children = order.passengers.filter(
        p => p.passengerType === 'CHD',
      ).length;
      const infants = order.passengers.filter(
        p => p.passengerType === 'INF',
      ).length;

      const parts: string[] = [];
      if (adults > 0)
        parts.push(
          `${adults} ${translations?.['b2b.txt.adultLabel'] || 'người lớn'}`,
        );
      if (children > 0)
        parts.push(
          `${children} ${translations?.['b2b.txt.childLabel'] || 'trẻ em'}`,
        );
      if (infants > 0)
        parts.push(
          `${infants} ${
            translations?.['b2b.txt.infantLabel'] || 'trẻ sơ sinh'
          }`,
        );

      return parts.join(', ');
    }

    // Fallback: sử dụng tổng quantity từ combo
    // Holiday chưa trả chi tiết passengers → hiển thị tổng
    const total = order.orderItems.reduce(
      (sum, item) => sum + item.combo.quantity,
      0,
    );
    return `${total} ${
      translations?.['b2b.txt.defaultAdultLabel'] || 'người lớn'
    }`; // Mặc định giả định là người lớn
  };

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <HeaderContent>
          <LeftSection>
            <InfoItem>
              <InfoLabel>
                {translations?.['b2b.txt.bookingCode'] || 'Mã đặt chỗ'}
              </InfoLabel>
              <InfoValue>{order.pnr || order.orderCode || '--'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                {translations?.['b2b.txt.passenger'] || 'Hành khách'}
              </InfoLabel>
              <InfoValue>{getPassengerCount() || '--'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                {translations?.['b2b.txt.bookingDate'] || 'Ngày đặt chỗ'}
              </InfoLabel>
              <InfoValue>
                {order.createdAt
                  ? moment(order.createdAt).format('DD/MM/YYYY HH:mm:ss')
                  : '--'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                {translations?.['b2b.txt.orderedBy'] || 'Người đặt'}
              </InfoLabel>
              <InfoValue>{order.agent || '--'}</InfoValue>
            </InfoItem>
          </LeftSection>
        </HeaderContent>

        <FooterBar>
          <FooterInner>
            <ButtonGroup>
              <RejectButton
                onClick={onReject}
                disabled={
                  actionLoading ||
                  order.subOrderState !== OrderStatus.PENDING_CONFIRMATION
                }
              >
                {translations?.['b2b.txt.reject'] || 'Từ chối'}
              </RejectButton>
              <ApproveButton
                onClick={onApprove}
                disabled={
                  actionLoading ||
                  order.subOrderState !== OrderStatus.PENDING_CONFIRMATION
                }
              >
                {translations?.['b2b.txt.approve'] || 'Đồng ý'}
              </ApproveButton>
            </ButtonGroup>
          </FooterInner>
        </FooterBar>
      </HeaderContainer>
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div`
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 24px;
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex: 1;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 16px 24px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  @media (max-width: 768px) {
    width: calc(50% - 12px);
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const InfoLabel = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1;
`;

const InfoValue = styled.div`
  color: #262626;
  font-size: 16px;
  line-height: 1.2;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const BaseButton = styled.button`
  height: 40px;
  padding: 0 32px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RejectButton = styled(BaseButton)`
  background: #fff;
  color: #ff4d4f;
  border: 1px solid #d9d9d9;
  box-shadow: none;

  &:hover:not(:disabled) {
    color: #ff4d4f;
    border-color: #ff4d4f;
  }
`;

const ApproveButton = styled(BaseButton)`
  background: var(
    --color-gradient,
    linear-gradient(90deg, #ff6b00 0%, #ff9900 100%)
  );
  color: #fff;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const FooterBar = styled.div`
  border-top: 1px solid #f0f0f0;
  padding: 16px 0;
  margin-top: 12px;
  background: #fff;
`;

const FooterInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

